import Fuse from "fuse.js";

import payers from "@/src/data/payers.json";
import { normalizeInsurancePlanIdentity } from "@/src/lib/insurance/match-valerie-insurance-plan";
import { logger } from "@/src/lib/logging/logger";
import { Coverage, CoverageType } from "@/src/models/coverage";

export function isHmoPlan(planText: string | null | undefined): boolean {
  return /\bHMO\b/i.test(planText ?? "");
}

export function isPpoPlan(planText: string | null | undefined): boolean {
  return /\bPPO\b/i.test(planText ?? "");
}

export function isEpoPlan(planText: string | null | undefined): boolean {
  return /\bEPO\b|exclusive provider/i.test(planText ?? "");
}

export function hasMissingSubscriberId(coverage: Coverage): boolean {
  const subscriberId = coverage.subscriberId?.trim() ?? "";

  return subscriberId === "" || subscriberId === "0";
}

export function isValidCoverage(coverage: Coverage | null | undefined): coverage is Coverage {
  if (!coverage) return false;

  if (hasMissingSubscriberId(coverage)) return false;

  return Boolean(coverage.payer?.trim() || coverage.plan?.trim());
}

export function hasInsuranceInformation(coverage: Coverage | null | undefined): boolean {
  if (!coverage) return false;
  if (coverage.coverageType === CoverageType.SELF_PAY) return true;

  const subscriberId = coverage.subscriberId?.trim();
  const hasSubscriberId = Boolean(subscriberId) && subscriberId !== "0";

  return Boolean(coverage.payer?.trim() || coverage.plan?.trim() || coverage.customerPayer?.trim() || hasSubscriberId);
}

export function cleanSubscriberId(subscriberId: string): string {
  return subscriberId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

function normalizedCoverageIdentity(value: string | null | undefined): string {
  return value ? cleanSubscriberId(value) : "";
}

/**
 * Returns true only when two coverages identify the same policy. Missing group
 * or plan information must not match a populated value and inherit its EHR link.
 * Prefer canonical payer IDs when both are available; otherwise require the
 * extracted payer names to match exactly after normalization.
 */
export function isSameInsurancePolicy(first: Partial<Coverage>, second: Partial<Coverage>): boolean {
  if (first.coverageType !== CoverageType.INSURANCE || second.coverageType !== CoverageType.INSURANCE) {
    return false;
  }

  const firstSubscriberId = normalizedCoverageIdentity(first.subscriberId);
  const secondSubscriberId = normalizedCoverageIdentity(second.subscriberId);
  const firstGroupNumber = normalizedCoverageIdentity(first.groupNumber);
  const secondGroupNumber = normalizedCoverageIdentity(second.groupNumber);
  const firstPlan = normalizedCoverageIdentity(first.plan);
  const secondPlan = normalizedCoverageIdentity(second.plan);

  if (
    !firstSubscriberId ||
    firstSubscriberId === "0" ||
    firstSubscriberId !== secondSubscriberId ||
    firstGroupNumber !== secondGroupNumber ||
    firstPlan !== secondPlan
  ) {
    return false;
  }

  const firstCustomerPayer = normalizedCoverageIdentity(first.customerPayer);
  const secondCustomerPayer = normalizedCoverageIdentity(second.customerPayer);

  if (firstCustomerPayer && secondCustomerPayer) {
    return firstCustomerPayer === secondCustomerPayer;
  }

  const firstPayer = normalizedCoverageIdentity(first.payer);
  const secondPayer = normalizedCoverageIdentity(second.payer);

  return Boolean(firstPayer && firstPayer === secondPayer);
}

/** Free text may identify a carrier only through a unique exact catalog name or alias. */
export function resolveReportedCoveragePayer(coverage: Coverage): string | null {
  if (coverage.customerPayer) return coverage.customerPayer;
  const name = normalizeInsurancePlanIdentity(coverage.payer);

  if (!name) return null;
  const matches = new Set(
    payers
      .filter((payer) =>
        [payer.name, ...(payer.otherName ?? [])].some((alias) => normalizeInsurancePlanIdentity(alias) === name)
      )
      .flatMap((payer) => (payer.primaryPayerId ? [payer.primaryPayerId] : []))
  );

  return matches.size === 1 ? [...matches][0] : null;
}

/** Portal reports identify a carrier/member pair; they do not supply chart plan or group numbers. */
export function matchesReportedCoverage(first: Coverage, second: Coverage): boolean {
  const identity = (coverage: Coverage) => ({
    coverageType: coverage.coverageType,
    payer: coverage.payer,
    customerPayer: resolveReportedCoveragePayer(coverage),
    subscriberId: coverage.subscriberId,
  });

  return isSameInsurancePolicy(identity(first), identity(second));
}

export function stripTrailingSuffix(cleaned: string): string[] {
  const variants = [cleaned];

  if (cleaned.length > 2) {
    variants.push(cleaned.slice(0, -2));
  }

  if (cleaned.length > 1) {
    variants.push(cleaned.slice(0, -1));
  }

  return variants;
}

export function coverageMatches(a: Coverage, b: Coverage): boolean {
  const asId = a.subscriberId?.trim() ?? "";
  const bsId = b.subscriberId?.trim() ?? "";

  if (!asId || asId === "0" || !bsId || bsId === "0") return false;

  const aCleaned = cleanSubscriberId(asId);
  const bCleaned = cleanSubscriberId(bsId);

  if (!aCleaned || !bCleaned) return false;

  if (aCleaned === bCleaned) return true;

  const aVariants = stripTrailingSuffix(aCleaned);
  const bVariants = stripTrailingSuffix(bCleaned);

  return aVariants.some((av) => bVariants.some((bv) => av === bv));
}

/**
 * Deduplicate coverages by subscriberID and sort by order
 * @param coverages - Array of coverages to process
 * @returns Deduplicated array of unique coverages sorted by order
 */
export function dedupedCoverages(coverages: Coverage[]): Coverage[] {
  if (!coverages || coverages.length === 0) {
    return [];
  }

  // Create a Map to deduplicate by subscriberID
  const uniqueCoverages = new Map<string, Coverage>();

  // Sort by order and deduplicate
  coverages
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((coverage) => {
      // Only process insurance coverage
      if (coverage.coverageType === CoverageType.INSURANCE) {
        const insuranceCoverage = coverage;

        if (insuranceCoverage.subscriberId) {
          // Only keep the first occurrence of each subscriberID
          if (!uniqueCoverages.has(insuranceCoverage.subscriberId)) {
            uniqueCoverages.set(insuranceCoverage.subscriberId, coverage);
          }
        } else {
          // If no subscriberID, add it to the map with a unique key
          uniqueCoverages.set(`no-id-${Math.random()}`, coverage);
        }
      }
    });

  return Array.from(uniqueCoverages.values());
}

/**
 * Search for coverages using a combination of exact and fuzzy matching, prioritizing key insurance identifiers.
 *
 * @param allCoverages - Array of all possible coverages to search through
 * @param query - Coverage object, search string, or null
 * @returns Array of matching coverages (first match for object queries, up to 20 for string queries)
 */
export function searchCoverages(allCoverages: Coverage[], query: Partial<Coverage> | string | null): Coverage[] {
  if (!allCoverages || allCoverages.length === 0) {
    return [];
  }

  if (query === null) {
    return [];
  }

  if (typeof query === "string") {
    const searchQuery = query.trim();

    if (!searchQuery) {
      return [];
    }

    const fuse = new Fuse(allCoverages, {
      keys: ["payer", "subscriberGivenName", "subscriberFamilyName", "order"],
      threshold: 0.5,
      ignoreLocation: true,
      isCaseSensitive: false,
    });

    const results = fuse.search(searchQuery, { limit: 20 });

    return results.map((result) => result.item);
  }

  const searchPayer = query.payer?.trim().toLowerCase() || "";
  const searchPlan = query.plan?.trim().toLowerCase() || "";
  const searchSubscriberId = query.subscriberId?.trim().toLowerCase() || "";
  const searchGroupNumber = query.groupNumber?.trim().toLowerCase() || "";
  const searchSubscriberGivenName = query.subscriberGivenName?.trim().toLowerCase() || "";
  const searchSubscriberFamilyName = query.subscriberFamilyName?.trim().toLowerCase() || "";

  // Check for valid search criteria
  const hasPayer = !!searchPayer;
  const hasPlan = !!searchPlan;
  const hasSubscriberId = !!searchSubscriberId;
  const hasGroupNumber = !!searchGroupNumber;
  const hasSubscriberName = !!(searchSubscriberGivenName && searchSubscriberFamilyName);

  if (!hasPayer && !hasPlan && !hasSubscriberId && !hasGroupNumber && !hasSubscriberName) {
    return [];
  }

  // 1. Exact matches by subscriber ID
  if (hasSubscriberId) {
    const subscriberIdMatches = allCoverages.filter(
      (cov) => cov.subscriberId?.trim().toLowerCase() === searchSubscriberId
    );

    if (subscriberIdMatches.length > 0) {
      // Return exact payer + subscriber ID match if available
      if (hasPayer) {
        const exactMatches = subscriberIdMatches.filter((cov) => cov.payer?.trim().toLowerCase() === searchPayer);

        if (exactMatches.length > 0) {
          logger.info("Coverages found by subscriber ID and payer", { exactMatches });

          return [exactMatches[0]]; // Return only the first match
        }
      }

      // If only one subscriber ID match, return it
      if (subscriberIdMatches.length === 1) {
        logger.info("Only one coverage with this subscriber ID, returning it", {
          fileName: "coverage-utils.ts",
          subscriberIdMatches,
        });

        return [subscriberIdMatches[0]]; // Return only the first match
      }

      // Multiple matches with same subscriber ID - find best match
      if (hasPayer || hasPlan || hasGroupNumber) {
        const searchPattern = [searchPayer, searchPlan, searchGroupNumber].filter(Boolean).join(" ");

        if (searchPattern) {
          const fuse = new Fuse(subscriberIdMatches, {
            keys: [
              { name: "payer", weight: 3 },
              { name: "plan", weight: 2 },
              { name: "groupNumber", weight: 1 },
            ],
            threshold: 0.4,
            ignoreLocation: true,
            includeScore: true,
          });

          const results = fuse.search(searchPattern, { limit: 20 });

          return results.length > 0 ? [results[0].item] : []; // Return only the first match
        }
      }

      return [subscriberIdMatches[0]];
    }

    return [];
  }

  // 2. Exact matches by payer and plan
  if (hasPayer && hasPlan) {
    const payerPlanMatches = allCoverages.filter(
      (cov) => cov.payer?.trim().toLowerCase() === searchPayer && cov.plan?.trim().toLowerCase() === searchPlan
    );

    if (payerPlanMatches.length > 0) return [payerPlanMatches[0]]; // Return only the first match
  }

  // 3. Fuzzy matching - payer first
  if (hasPayer) {
    const fuse = new Fuse(allCoverages, {
      keys: [{ name: "payer", weight: 1 }],
      threshold: 0.8,
      ignoreLocation: true,
      includeScore: true,
    });

    const results = fuse.search(searchPayer, { limit: 20 });

    if (results.length > 0 && results[0].score && results[0].score < 0.3) {
      logger.info("Coverages found by payer", { results });

      return [results[0].item]; // Return only the first match
    }
  }

  // 4. Subscriber name-based fuzzy search
  if (hasSubscriberName && !hasPayer && !hasPlan && !hasGroupNumber) {
    const subscriberNamePattern = [searchSubscriberGivenName, searchSubscriberFamilyName].filter(Boolean).join(" ");

    if (subscriberNamePattern) {
      const fuse = new Fuse(allCoverages, {
        keys: [
          { name: "subscriberGivenName", weight: 2 },
          { name: "subscriberFamilyName", weight: 2 },
        ],
        threshold: 0.5,
        ignoreLocation: true,
        includeScore: true,
      });

      const results = fuse.search(subscriberNamePattern, { limit: 20 });

      if (results.length > 0) {
        logger.info("Coverages found by subscriber name", { results });

        return [results[0].item]; // Return only the first match
      }
    }
  }

  // 5. Combined fuzzy search as last resort
  if (hasPayer || hasPlan || hasGroupNumber) {
    const searchPattern = [searchPayer, searchPlan, searchGroupNumber].filter(Boolean).join(" ");

    if (searchPattern) {
      const fuse = new Fuse(allCoverages, {
        keys: [
          { name: "payer", weight: 3 },
          { name: "plan", weight: 2.5 },
          { name: "groupNumber", weight: 1.5 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
        includeScore: true,
      });

      const results = fuse.search(searchPattern, { limit: 20 });

      if (results.length > 0 && results[0].score !== undefined && results[0].score < 0.4) {
        const bestMatch = results[0].item;

        logger.info("Coverages found by combined search", { bestMatch });

        return [bestMatch]; // Return only the first match
      }
    }
  }

  return [];
}

/**
 * Legacy function for backward compatibility - returns the best matching coverage or null
 * @deprecated Use searchCoverages instead which returns an array
 */
export function findBestCoverageMatch(
  allCoverages: Coverage[],
  query: Partial<Coverage> | string | null
): Coverage | null {
  const results = searchCoverages(allCoverages, query);

  return results.length > 0 ? results[0] : null;
}
